import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import TextField from '../textField';
import SelectField from '../selectField';
import BooleanField from '../booleanField';
import { useTranslation } from 'react-i18next';
import MultiSelectFree from '../muiltipleSelectFree';
import NumberField from '../numberField';
import SettingConditions from './settingConditions';

const FIELD_TYPES = [
  { value: 's', label: 'String' },
  { value: 't', label: 'Multiline text' },
  { value: 'n', label: 'Numeric' },
  { value: 'b', label: 'Boolean' },
  { value: 'l', label: 'String from a list' },
  { value: 'ml', label: 'Multiple string values' },
  { value: 'json', label: 'JSON' },
  { value: 'cron', label: 'cron' },
  { value: 'regex', label: 'regex' },
];
const VALUE_OPTIONS_TYPE = [
  'acceptedFileTypes',
  'dataExtractionLlms',
  'conversationLlms',
  'openAiConversationModels',
  'openAiDataExtractionModels',
  'azureOpenAiConversationModels',
  'azureOpenAiDataExtractionModels',
  'geminiAiConversationModels',
  'geminiAiDataExtractionModels',
  'claudeAiConversationModels',
  'claudeAiDataExtractionModels',
  'conversationResponseTone',
  'textExtractionApi',
  'pdfConversionApi',
  'translationApi',
  'embeddingLlms',
  'openAiEmbeddingModels',
  'azureOpenAiEmbeddingModels',
  'geminiAiEmbeddingModels',
];

type Props = {
  sectionName: string;
  settingDef: any;
  settingNames: any[];
  open: boolean;
  onCancel: () => void;
  onSubmit: (section: string, settingDef: any) => void;
};

const SettingDefDialog = ({ sectionName, settingDef, settingNames = [], open, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();

  const [name, setName] = useState(settingDef?.name);
  const [label, setLabel] = useState(settingDef?.label);
  const [type, setType] = useState(settingDef?.type);
  const [defaultValue, setDefaultValue] = useState(settingDef?.defaultValue);
  const [sensitive, setSensitive] = useState(settingDef?.sensitive);
  const [required, setRequired] = useState(settingDef?.required);
  const [disabled, setDisabled] = useState(settingDef?.disabled);
  const [valueOptionsType, setValueOptionsType] = useState(
    typeof settingDef?.valueOptions === 'string' ? 'predefined' : 'custom',
  );
  const [valueOptionsListName, setValueOptionsListName] = useState(settingDef?.valueOptions);
  const [valueOptionsList, setValueOptionsList] = useState(
    typeof settingDef?.valueOptions === 'string' ? [] : settingDef?.valueOptions || [],
  );
  const [minValue, setMinValue] = useState<number>(settingDef?.minValue);
  const [maxValue, setMaxValue] = useState<number>(settingDef?.maxValue);
  const [conditions, setConditions] = useState<any[]>(settingDef?.conditions || []);

  const [errors, setErrors] = useState({} as any);

  const onAddCondition = (condition: any) => {
    setConditions([condition, ...conditions]);
  };

  const onDeleteCondition = (condition: any) => {
    setConditions(
      conditions.filter((c) => c.settingName !== condition.settingName || c.settingValue !== condition.settingValue),
    );
  };

  const addSettingDef = () => {
    let hasError = false;
    const errors: any = {};
    if (!name) {
      errors.name = t('common.errors.requiredField');
      hasError = true;
    }
    if (settingDef?.name !== name && settingNames.find((sn) => sn.name === name)) {
      errors.name = t('Already exists');
      hasError = true;
    }

    if (!label) {
      errors.label = t('common.errors.requiredField');
      hasError = true;
    }

    if (!type) {
      errors.type = t('common.errors.requiredField');
      hasError = true;
    }

    setErrors(errors);
    if (!hasError) {
      onSubmit(sectionName, {
        name,
        label,
        type,
        defaultValue,
        sensitive,
        required,
        disabled,
        valueOptions: valueOptionsType === 'predefined' ? valueOptionsListName : valueOptionsList,
        minValue,
        maxValue,
        conditions,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth={'md'}>
      <DialogTitle>Setting definition</DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={1}>
          <Grid size={12}>
            <TextField label="Name" value={name} required error={errors.name} onChange={setName} type="text" />
          </Grid>
          <Grid size={12}>
            <TextField label="Label" value={label} required error={errors.label} onChange={setLabel} type="text" />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <SelectField
              label="Type"
              required
              options={FIELD_TYPES}
              value={type}
              onChange={setType}
              error={errors.type}
            />
          </Grid>
          {(type === 'l' || type === 'ml') && (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 8,
              }}
            >
              <FormLabel required>{'Value options'}</FormLabel>
              <RadioGroup
                value={valueOptionsType}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const newValueOptionsType = (event.target as HTMLInputElement).value;
                  setValueOptionsList(
                    typeof settingDef?.valueOptions === 'string' ? [] : settingDef?.valueOptions || [],
                  );
                  setValueOptionsType(newValueOptionsType);
                }}
                row
              >
                <FormControlLabel
                  value={'predefined'}
                  control={<Radio size="small" />}
                  label={'Predefined list'}
                ></FormControlLabel>
                <FormControlLabel
                  value={'custom'}
                  control={<Radio size="small" />}
                  label={'Custom values'}
                ></FormControlLabel>
              </RadioGroup>

              {valueOptionsType === 'predefined' && (
                <SelectField
                  label="Value list"
                  required
                  options={VALUE_OPTIONS_TYPE.map((o) => ({ value: o, label: t(`config.valueOptionTypes.${o}`) }))}
                  value={valueOptionsListName}
                  onChange={setValueOptionsListName}
                  error={errors.valueOptionsListName}
                />
              )}
              {valueOptionsType === 'custom' && (
                <MultiSelectFree
                  label="Value list"
                  required
                  options={[]}
                  value={valueOptionsList}
                  onChange={setValueOptionsList}
                  error={errors.valueOptionsList}
                />
              )}
            </Grid>
          )}
          {type === 'n' && (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <NumberField label="Min value" value={minValue} required error={errors.minValue} onChange={setMinValue} />
            </Grid>
          )}
          {type === 'n' && (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <NumberField label="Max Value" value={maxValue} required error={errors.maxValue} onChange={setMaxValue} />
            </Grid>
          )}
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <BooleanField label="Is sensitive" value={sensitive} error={errors.sensitive} onChange={setSensitive} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <BooleanField label="Is required" value={required} error={errors.required} onChange={setRequired} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <BooleanField label="Is disabled" value={disabled} error={errors.disabled} onChange={setDisabled} />
          </Grid>
          {(type === 's' || type === 't' || type === 'l' || type === 'ml' || type === 'cron' || type === 'regex') && (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <TextField
                label="Default Value"
                value={defaultValue}
                error={errors.defaultValue}
                onChange={setDefaultValue}
                type="text"
              />
            </Grid>
          )}
          {type === 'n' && (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <NumberField
                label="Default Value"
                value={defaultValue}
                error={errors.defaultValue}
                onChange={setDefaultValue}
              />
            </Grid>
          )}
          {type === 'b' && (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <BooleanField
                label="Default Value"
                value={defaultValue}
                error={errors.defaultValue}
                onChange={setDefaultValue}
              />
            </Grid>
          )}
          <Grid size={12}>
            <SettingConditions
              conditions={conditions}
              settingNames={settingNames}
              maxHeight={300}
              onAddCondition={onAddCondition}
              onDeleteCondition={onDeleteCondition}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onCancel}>
          {t('common.buttons.cancel')}
        </Button>
        <Button size="small" color="primary" variant="contained" onClick={addSettingDef}>
          {t('common.buttons.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingDefDialog;
